/**
 * SS card / row background tone from unit outcomes + job workflow.
 *
 * Rules (Result palette — not due-date clock):
 * 1. Any unit FAIL → Fail (red)
 * 2. All expected units PASS → Pass (green)
 * 3. Needs schedule / scheduled / incomplete / site visit / partial → Incomplete (yellow)
 * 4. Job FAILED / COMPLETED used when no unit data yet
 */

import { getJobRowStyle } from './jobStatusColor';

/**
 * @param {Array<{ outcome?: string, ss_unit_id?: number|string }|string>} states
 * @param {number} [expectedUnitCount]
 * @returns {'FAIL'|'PASS'|'INCOMPLETE'|null}
 */
export function rollupUnitOutcomes(states, expectedUnitCount) {
  const list = Array.isArray(states) ? states : [];
  const outcomes = list.map((s) =>
    String(typeof s === 'string' ? s : s?.outcome || 'NOT_TESTED')
      .trim()
      .toUpperCase()
  );

  if (outcomes.some((o) => o === 'FAIL' || o === 'FAILED')) return 'FAIL';

  const isPass = (o) => o === 'PASS' || o === 'PASSED';
  const isActive = (o) =>
    isPass(o) ||
    o === 'INCOMPLETE' ||
    o === 'IN_PROGRESS' ||
    o === 'PARTIAL' ||
    o === 'STARTED';

  const expected = Number(expectedUnitCount);
  if (Number.isFinite(expected) && expected > 0) {
    const byUnit = new Map();
    list.forEach((s) => {
      if (typeof s === 'string') return;
      const id = s?.ss_unit_id != null ? String(s.ss_unit_id) : null;
      if (!id) return;
      byUnit.set(id, String(s.outcome || 'NOT_TESTED').trim().toUpperCase());
    });
    if (byUnit.size >= expected) {
      const vals = [...byUnit.values()];
      if (vals.length >= expected && vals.every(isPass)) return 'PASS';
    }
    if ([...byUnit.values()].some(isActive) || outcomes.some(isActive)) return 'INCOMPLETE';
    return null;
  }

  if (outcomes.length && outcomes.every(isPass)) return 'PASS';
  if (outcomes.some(isActive)) return 'INCOMPLETE';
  return null;
}

/**
 * Status key suitable for getJobRowStyle Result colours:
 * FAILED (red) | COMPLETED (green) | IN_PROGRESS (yellow incomplete)
 *
 * @param {{ jobStatus?: string|null, unitStates?: array, expectedUnitCount?: number }} args
 */
export function resolveSsResultStatus({ jobStatus, unitStates, expectedUnitCount } = {}) {
  const unit = rollupUnitOutcomes(unitStates, expectedUnitCount);
  if (unit === 'FAIL') return 'FAILED';
  if (unit === 'PASS') return 'COMPLETED';
  if (unit === 'INCOMPLETE') return 'IN_PROGRESS';

  const st = String(jobStatus || '')
    .trim()
    .toUpperCase();
  if (st === 'FAILED' || st === 'REJECTED') return 'FAILED';
  if (st === 'COMPLETED') return 'COMPLETED';

  // Needs schedule, Mail, Scheduled, Accepted, site visit, awaiting reply, due-only, …
  return 'IN_PROGRESS';
}

/** Table row / border-left style for BuildingInspections SS lines. */
export function getSsResultRowStyle(args, daysUntilDue) {
  return getJobRowStyle(resolveSsResultStatus(args), daysUntilDue, false);
}

/** Order page SS card border + background. */
export function getSsResultCardClass(args) {
  const status = resolveSsResultStatus(args);
  if (status === 'FAILED') return 'border-red-500 bg-red-50';
  if (status === 'COMPLETED') return 'border-green-500 bg-green-50';
  return 'border-yellow-400 bg-yellow-50';
}
